import axios from 'axios';
import { SearchFAQArticlesPayload, SearchFAQArticlesResponse } from './search-results.types';
import { Article } from './subtopics.types';

const ROOT_SELECTOR = '.search-results-page';

export function initSearchResults(): void {
    document.querySelectorAll<HTMLElement>(ROOT_SELECTOR).forEach(initSearchResultsRoot);
}

function initSearchResultsRoot(searchResultsPage: HTMLElement): void {
    let isLoading = false;
    const ITEMS_PER_PAGE = 10;

    const searchResultsContainer = searchResultsPage.querySelector('.questions-list__items');
    const searchResultsTitle = searchResultsPage.querySelector('.header-with-search__title-single');
    const searchResultsCountNumber = searchResultsPage.querySelector('.header-with-search__count-number');
    const loadMoreBtn = searchResultsPage.querySelector('.questions-list__load-more') as HTMLButtonElement | null;

    let query = searchResultsPage?.getAttribute('data-query') || '';
    let hasMore = searchResultsPage?.getAttribute('data-has-more') === 'True' || false;
    let currentPage = parseInt(searchResultsPage?.getAttribute('data-next-page') || '1', 10);

    const fetchSearchFaqArticlesResults = async (page: number): Promise<SearchFAQArticlesResponse> => {
        const payload: SearchFAQArticlesPayload = {
            query: query,
            pageNumber: page,
            pageSize: ITEMS_PER_PAGE,
        };

        try {
            const response = await axios.post<SearchFAQArticlesResponse>('/api/articles/search', payload);

            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const renderArticle = ({ title, url }: Article): string => {
        const absoluteLink = new URL(url.replace('~', ''), window.location.origin).toString();
        return `
            <li class="questions-list__item">
                <a class="questions-list__card" href="${absoluteLink}">
                    <span class="questions-list__text">${title}</span>
                    <img class="questions-list__chevron" src="/assets/img/arrow-right.svg" alt="" aria-hidden="true" />
                </a>
            </li>
        `.trim();
    };

    const appendArticles = (articles: Article[]): void => {
        if (!searchResultsContainer) return;

        const fragment = document.createDocumentFragment();
        const temp = document.createElement('ul');
        temp.innerHTML = articles.map(renderArticle).join('');

        while (temp.firstChild) {
            fragment.appendChild(temp.firstChild);
        }

        searchResultsContainer.appendChild(fragment);
    };

    function setLoadMoreVisibility(visible: boolean): void {
        if (!loadMoreBtn) return;
        if (visible) {
            loadMoreBtn.classList.remove('hidden');
        } else {
            loadMoreBtn.classList.add('hidden');
        }
    }

    const setLoadingState = (loading: boolean): void => {
        if (!loadMoreBtn) return;
        isLoading = loading;
        (loadMoreBtn as HTMLButtonElement).disabled = loading;
        loadMoreBtn.textContent = loading ? 'Loading...' : 'Load more';
    };

    const loadMore = async (): Promise<void> => {
        if (isLoading || !hasMore) return;

        setLoadingState(true);

        try {
            const response = await fetchSearchFaqArticlesResults(currentPage);

            appendArticles(response.items);

            hasMore = response.hasMore;
            currentPage = response.nextPage;

            searchResultsCountNumber.innerHTML = response.count.toString();

            searchResultsTitle.innerHTML = query;
            setLoadMoreVisibility(hasMore);
        } catch (error) {
            console.error('Failed to fetch help topics:', error);
        } finally {
            setLoadingState(false);
        }
    };

    loadMoreBtn?.addEventListener('click', () => loadMore());
}
